import {
  AccessorialCode,
  DomainReference,
  Optional,
} from '@rygen/rygen-ui/model';

import i18n from '@/i18n';

import { ShipmentStopType } from '../shipment/ShipmentStopType';
import { ShipOrder } from '../ShipOrder';
import { ShippingPoint } from '../ShippingPoint';

import { AddStopToShipmentPayload } from './events/AddStopToShipmentPayload';
import { PlanningStatus } from './PlanningStatus';
import { ProposedShipmentLineItem } from './ProposedShipmentLineItem';
import { ProposedShipmentStop } from './ProposedShipmentStop';
import { ResequenceState } from './ResequenceState';
import { ValidShipOrder } from './ValidShipOrder';
import { ValidShipOrderShippingPoint } from './ValidShipOrderShippingPoint';

export class ProposedShipment {
  static readonly MAX_WEIGHT_LBS = 45000;
  static readonly MAX_PALLET_COUNT = 30;
  static readonly MAX_LINEAR_FEET = 53;
  static readonly MAX_VOLUME_CUBIC_FEET = 3800;

  /**
   * An identifier for this shipment
   *
   * _Note:_ This is **not** an id but kinda works like one. It should be unique
   * (within a route plan) and unchanging but this model isn't persisted so
   * it's not a primary key to anything.
   **/
  identifier: number;
  owner?: DomainReference;
  stops: ProposedShipmentStop[] = [];

  readonly shipOrders: ValidShipOrder[] = [];
  readonly validationErrors: string[] = [];
  readonly validationWarnings: string[] = [];

  static colorOptions = [
    '#3D40E1',
    '#CA6899',
    '#D2C72D',
    '#41D78C',
    '#DB9957',
    '#96CA68',
    '#2DD2C7',
    '#5799DB',
    '#85C270',
    '#9966CC',
  ];

  constructor(opts?: Partial<ProposedShipment>) {
    const identifier = Math.max(opts?.identifier ?? 1, 1);
    this.identifier = identifier;

    if (opts?.owner) {
      this.owner = new DomainReference(opts.owner);
    }
    if (opts?.shipOrders) {
      this.shipOrders = opts.shipOrders.map((so) => new ValidShipOrder(so));
    }
    if (opts?.stops) {
      this.stops = opts.stops.map(
        (stop) =>
          new ProposedShipmentStop({
            ...stop,
            shipmentIdentifier: identifier,
          }),
      );
    }

    if (this.stops.length) {
      this.updateStopSequence();
    }
  }

  static fromShipOrder(identifier: number, shipOrder: ValidShipOrder) {
    const origin = new ProposedShipmentStop({
      shipmentIdentifier: identifier,
      sequenceNumber: 1,
      stopType: ShipmentStopType.PICKUP,
      location: shipOrder.origin.location,
      shipOrderShippingPoints: [shipOrder.origin],
    });

    const destination = new ProposedShipmentStop({
      shipmentIdentifier: identifier,
      sequenceNumber: 2,
      stopType: ShipmentStopType.DROP_OFF,
      location: shipOrder.destination.location,
      shipOrderShippingPoints: [shipOrder.destination],
    });

    shipOrder.origin.planningStatus = PlanningStatus.PLANNED;
    shipOrder.destination.planningStatus = PlanningStatus.PLANNED;
    return new ProposedShipment({
      identifier: identifier,
      shipOrders: [shipOrder],
      stops: [origin, destination],
    });
  }

  static copy(proposedShipment: ProposedShipment): ProposedShipment {
    return new ProposedShipment(proposedShipment);
  }

  get isEmpty(): boolean {
    return !this.shipOrders.length && !this.origin && !this.destination;
  }

  get isHazmat(): boolean {
    return this.lineItems.some((li) => li.isHazmat);
  }

  get shipOrderIds(): number[] {
    return this.shipOrders.map((so) => so.id);
  }

  get lineItems(): ProposedShipmentLineItem[] {
    const lineItems: ProposedShipmentLineItem[] = [];

    for (const shipOrder of this.shipOrders) {
      const { pickupStopSequenceNumber, dropOffStopSequenceNumber } =
        this.determineShipOrderLineItemStopSequenceNumbers(shipOrder);
      for (const soli of shipOrder.lineItems) {
        if (!soli.id) {
          throw new Error(
            'Invalid State - could not determine shiporder line item id',
          );
        }

        const proposedShipmentLineItem = new ProposedShipmentLineItem({
          ...soli,
          shipOrderId: shipOrder.id,
          shipOrderLineItemId: soli.id,
          pickupStopSequenceNumber,
          dropOffStopSequenceNumber,
        });

        lineItems.push(proposedShipmentLineItem);
      }
    }

    return lineItems;
  }

  get origin(): ProposedShipmentStop | undefined {
    const stop = this.stops[0];
    return stop.stopType === ShipmentStopType.PICKUP ? stop : undefined;
  }

  get destination(): ProposedShipmentStop | undefined {
    const stop = this.stops[this.stops.length - 1];
    return stop.stopType === ShipmentStopType.DROP_OFF ? stop : undefined;
  }

  get isExistingShipment() {
    return this.shipOrders.some((s) => !!s.shipmentId);
  }

  get requestedAccessorials(): AccessorialCode[] {
    return [];
  }

  /** A unique hex code for the shipment, including the `#` */
  get color() {
    const numColorOptions = ProposedShipment.colorOptions.length;
    const index = (this.identifier - 1) % numColorOptions;
    return ProposedShipment.colorOptions[index];
  }

  getLineItemsForStop(stop: ShippingPoint): ProposedShipmentLineItem[] {
    if (!ProposedShipmentStop.isProposedShipmentStop(stop)) {
      throw new Error('stop should be a ProposedShipmentStop');
    }

    const isPickup = stop.stopType === ShipmentStopType.PICKUP;
    const seqNumKey = isPickup
      ? 'pickupStopSequenceNumber'
      : 'dropOffStopSequenceNumber';
    return this.lineItems.filter((l) => l[seqNumKey] === stop.sequenceNumber);
  }

  hasShipOrder(shipOrder: ShipOrder): boolean {
    return !!shipOrder.id && this.shipOrderIds.includes(shipOrder.id);
  }

  hasOriginStop(shipOrder: ShipOrder): boolean {
    const originLocation = shipOrder.origin?.location;
    return this.stops.some((s) => s.location.hasSameAddress(originLocation));
  }

  hasDestinationStop(shipOrder: ShipOrder): boolean {
    const destinationLocation = shipOrder.destination?.location;
    return this.stops.some((s) =>
      s.location.hasSameAddress(destinationLocation),
    );
  }

  hasBothStops(shipOrder: ShipOrder): boolean {
    return this.hasOriginStop(shipOrder) && this.hasDestinationStop(shipOrder);
  }

  addShipOrder(shipOrder: ValidShipOrder): void {
    if (this.shipOrderIds.includes(shipOrder.id)) {
      // If `shipOrder` was partially on this shipment, remove it and readd the whole ship order
      this.removeShipOrder(shipOrder);
    }

    this.shipOrders.push(shipOrder);
    this.addShipOrderShippingPoint(shipOrder.origin);

    const { stop: stopForPrereq } = this.findStopByShipOrderShippingPoint(
      shipOrder.origin,
    );
    this.addShipOrderShippingPoint(shipOrder.destination, stopForPrereq);
  }

  addPartialShipOrder(shipOrder: ValidShipOrder): void {
    if (this.shipOrderIds.includes(shipOrder.id)) {
      return;
    }

    this.shipOrders.push(shipOrder);
    if (this.hasOriginStop(shipOrder)) {
      this.addShipOrderShippingPoint(shipOrder.origin);
    } else {
      this.addShipOrderShippingPoint(shipOrder.destination, this.origin);
    }
  }

  addShipOrderShippingPoint(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
    stopForPrereq?: ProposedShipmentStop,
  ): void {
    // only consider stops that happen after milestones are fulfilled
    // (i.e. dropoffs can't happen before pickup)
    let stopsAfterPrereq: ProposedShipmentStop[] = this.stops;

    if (stopForPrereq) {
      stopsAfterPrereq = stopsAfterPrereq.filter(
        (s) => s.sequenceNumber > stopForPrereq.sequenceNumber,
      );
    }

    stopsAfterPrereq = [...stopsAfterPrereq].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );

    // Find possible merge opportunities
    const stopToCombineWith = stopsAfterPrereq.find((existingStop) => {
      const haveSameStopType =
        existingStop.stopType === shipOrderShippingPoint.stopType;

      const haveSameAddress = existingStop.location.hasSameAddress(
        shipOrderShippingPoint.location,
      );

      const haveOverlappingDates =
        existingStop.requestedAppointmentRange.isOverlappedBy(
          shipOrderShippingPoint.requestedAppointment,
        );

      return (
        haveSameStopType &&
        (existingStop.isPlaceholderStop ||
          (haveSameAddress && haveOverlappingDates))
      );
    });

    if (stopToCombineWith) {
      stopToCombineWith.addShipOrderShippingPoint(shipOrderShippingPoint);
      if (stopToCombineWith._requestedAppointmentRange) {
        stopToCombineWith._requestedAppointmentRange =
          stopToCombineWith._requestedAppointmentRange.intersectionWith(
            shipOrderShippingPoint.requestedAppointment,
          );
      }
    } else {
      let indexToInsert = this.stops.length;

      for (const existingStop of stopsAfterPrereq) {
        const existingApptRange = existingStop.requestedAppointmentRange;
        const newApptRange = shipOrderShippingPoint.requestedAppointment;

        const isExistingRangeCompletelyAfterNewRange =
          !!newApptRange.end && existingApptRange.isAfterRange(newApptRange);

        const isExistingRangePartiallyAfterNewRange =
          !!newApptRange.start &&
          !!existingApptRange.start &&
          newApptRange.start < existingApptRange.start;

        const shouldGoBeforeExistingStop =
          existingApptRange.isEmpty ||
          isExistingRangeCompletelyAfterNewRange ||
          isExistingRangePartiallyAfterNewRange;

        if (shouldGoBeforeExistingStop) {
          indexToInsert = this.stops.indexOf(existingStop);
          break;
        }
      }

      // create new stop
      const newStop = new ProposedShipmentStop({
        shipmentIdentifier: this.identifier,
        shipOrderShippingPoints: [shipOrderShippingPoint],
        requestedAppointmentRange: shipOrderShippingPoint.requestedAppointment,
        sequenceNumber: -1,
      });

      // insert at the proper position
      this.stops.splice(indexToInsert, 0, newStop);
      this.updateStopSequence();
    }

    shipOrderShippingPoint.planningStatus = PlanningStatus.PLANNED;
  }

  removeShipOrder(shipOrder: ShipOrder): void {
    const localShipOrderIndex = this.shipOrders.findIndex(
      (so) => so.id === shipOrder.id,
    );
    if (localShipOrderIndex === -1) {
      console.warn('Could not find ship order to remove', shipOrder);
      return;
    }

    const [removedShipOrder] = this.shipOrders.splice(localShipOrderIndex, 1);
    this.removeShipOrderShippingPoint(removedShipOrder.origin);
    this.removeShipOrderShippingPoint(removedShipOrder.destination);
  }

  validateStopResequencing(
    oldSequenceNumber: number,
    newSequenceNumber: number,
  ): ResequenceState {
    const stop = this.stops.find((s) => s.sequenceNumber === oldSequenceNumber);
    if (!stop) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.doesNotExist',
        ),
      };
    }

    const prevStop =
      oldSequenceNumber > 1
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber - 1)
        : undefined;
    const nextStop =
      oldSequenceNumber < this.stops.length
        ? this.stops.find((s) => s.sequenceNumber === oldSequenceNumber + 1)
        : undefined;
    const stopType = stop.stopType;
    if (
      (stopType === ShipmentStopType.PICKUP &&
        newSequenceNumber === this.stops.length) ||
      (stopType === ShipmentStopType.DROP_OFF &&
        oldSequenceNumber === this.stops.length &&
        prevStop?.stopType === ShipmentStopType.PICKUP)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.lastStop',
        ),
      };
    } else if (
      (stopType === ShipmentStopType.DROP_OFF && newSequenceNumber === 1) ||
      (stopType === ShipmentStopType.PICKUP &&
        oldSequenceNumber === 1 &&
        nextStop?.stopType === ShipmentStopType.DROP_OFF)
    ) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.firstStop',
        ),
      };
    }

    // line items being dropped off at this stop
    const lineItemsBeingDroppedOff = this.lineItems.filter(
      (li) => li.dropOffStopSequenceNumber === oldSequenceNumber,
    );
    // the pickup stops for all the line items that are being dropped off at this stop
    const relatedPickupStops = this.stops.filter((stop) =>
      lineItemsBeingDroppedOff.some(
        (li) => li.pickupStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the highest sequence number of all stops that are pickups for this drop
    // the stop being modified cannot be moved before this sequence number
    const minSeqNum = relatedPickupStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.max(a, b), -1);

    // line items being picked up at this stop
    const lineItemsBeingPickedUp = this.lineItems.filter(
      (li) => li.pickupStopSequenceNumber === oldSequenceNumber,
    );
    // the drop off stops for all the line items that are being picked up at this stop
    const relatedDropoffStops = this.stops.filter((stop) =>
      lineItemsBeingPickedUp.some(
        (li) => li.dropOffStopSequenceNumber === stop.sequenceNumber,
      ),
    );
    // the lowest sequence number of all stops that are dropoffs for this pickup
    // the stop being modified cannot be moved after this sequence number
    const maxSeqNum = relatedDropoffStops
      .map((stop) => stop.sequenceNumber)
      .reduce((a, b) => Math.min(a, b), this.stops.length + 1);

    if (newSequenceNumber <= minSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.beforeRelatedPickup',
        ),
      };
    } else if (newSequenceNumber >= maxSeqNum) {
      return {
        canResequence: false,
        canResequenceReason: i18n.global.t(
          'multistop.cantResequenceReason.afterRelatedDropoff',
        ),
      };
    } else {
      return {
        canResequence: true,
        canResequenceReason: '',
      };
    }
  }

  resequenceStop(oldSequenceNumber: number, newSequenceNumber: number): void {
    const { canResequence, canResequenceReason } =
      this.validateStopResequencing(oldSequenceNumber, newSequenceNumber);

    if (!canResequence) {
      throw new Error(canResequenceReason);
    }

    const stopOldIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === oldSequenceNumber,
    );
    if (stopOldIndex === -1) {
      throw new Error(
        `Could not find stop with sequenceNumber ${oldSequenceNumber}`,
      );
    }
    const stopNewIndex = newSequenceNumber - 1;

    const stops = [...this.stops];
    const [stop] = stops.splice(stopOldIndex, 1);
    stops.splice(stopNewIndex, 0, stop);
    for (let i = 0; i < stops.length; i++) {
      stops[i].sequenceNumber = i + 1;
    }
    this.stops.sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  }

  removeShipOrderShippingPoint(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
  ): void {
    const { stopIndex, stop } = this.findStopByShipOrderShippingPoint(
      shipOrderShippingPoint,
    );

    if (stop === undefined) {
      // The ship order was only partially on this shipment
      return;
    }

    // remove ship order shipping point from shipment stop
    const shipOrderShippingPointIndex = stop.shipOrderShippingPoints
      .map((x) => x.id)
      .findIndex((x) => x === shipOrderShippingPoint.id);
    if (shipOrderShippingPointIndex === -1) {
      throw new Error(
        'Illegal State - could not find shipOrderShippingPoint with id=' +
          shipOrderShippingPoint.id,
      );
    }
    stop.shipOrderShippingPoints.splice(shipOrderShippingPointIndex, 1);

    // remove stop from shipment if it is empty and its an intermediate stop
    const isOriginOrDestination =
      stop.sequenceNumber === 1 || stop.sequenceNumber === this.stops.length;
    if (!isOriginOrDestination && stop.shipOrderShippingPoints.length === 0) {
      this.stops.splice(stopIndex, 1);
      this.updateStopSequence();
    }
  }

  findStopByShipOrderShippingPoint(
    shipOrderShippingPoint?: ValidShipOrderShippingPoint,
  ): { stopIndex: number; stop?: ProposedShipmentStop } {
    let stopIndex = -1;

    const shipOrderShippingPointId = shipOrderShippingPoint?.id;
    if (shipOrderShippingPointId) {
      stopIndex = this.stops.findIndex((stop) =>
        stop.shipOrderShippingPointIds.includes(shipOrderShippingPointId),
      );
    }

    const stop: ProposedShipmentStop | undefined =
      stopIndex !== -1 ? this.stops[stopIndex] : undefined;

    return { stopIndex, stop };
  }

  determineShipOrderLineItemStopSequenceNumbers(shipOrder: ValidShipOrder): {
    pickupStopSequenceNumber?: number;
    dropOffStopSequenceNumber?: number;
  } {
    let pickupStopSequenceNumber: number | undefined;
    let dropOffStopSequenceNumber: number | undefined;

    for (const shipmentStop of this.stops) {
      const stopIncludesOrigin =
        shipmentStop.shipOrderShippingPointIds.includes(shipOrder.origin.id);
      if (stopIncludesOrigin) {
        pickupStopSequenceNumber = shipmentStop.sequenceNumber;
      }

      const stopIncludesDestination =
        shipmentStop.shipOrderShippingPointIds.includes(
          shipOrder.destination.id,
        );
      if (stopIncludesDestination) {
        dropOffStopSequenceNumber = shipmentStop.sequenceNumber;
      }

      if (pickupStopSequenceNumber && dropOffStopSequenceNumber) {
        break;
      }
    }

    return { pickupStopSequenceNumber, dropOffStopSequenceNumber };
  }

  addStop({ location, stopType }: AddStopToShipmentPayload) {
    const newStop = new ProposedShipmentStop({
      shipmentIdentifier: this.identifier,
      sequenceNumber: -1,
      stopType,
      location,
    });

    // The new stop is added right before the destination
    const index = this.stops.length - 1;
    this.stops.splice(index, 0, newStop);
    this.updateStopSequence();
  }

  removeStop(sequenceNumber: number): Optional<ProposedShipmentStop> {
    const stopIndex = this.stops.findIndex(
      (s) => s.sequenceNumber === sequenceNumber,
    );
    if (stopIndex === -1) {
      console.warn(`Could not find stop with sequenceNumber ${sequenceNumber}`);
      return;
    }

    // For origin/destination, we don't actually remove the stop. We remove the
    // location and ship orders from the stop, but leave the empty stop
    // as a placeholder
    let removedStop: ProposedShipmentStop;
    if (sequenceNumber === 1 || sequenceNumber === this.stops.length) {
      removedStop = this.stops[stopIndex];
      removedStop.location = undefined;
    } else {
      [removedStop] = this.stops.splice(stopIndex, 1);
    }

    [...removedStop.shipOrderShippingPoints].forEach((sosp) => {
      this.removeShipOrderShippingPoint(sosp);
      // if `sosp` is the only shipping point from the ship order, we have to
      // remove the ship order itself from the shipment
      this.maybeRemoveContainingShipOrder(sosp);
    });

    this.updateStopSequence();
    return removedStop;
  }

  maybeRemoveContainingShipOrder(
    shipOrderShippingPoint: ValidShipOrderShippingPoint,
  ) {
    const shipOrder = this.shipOrders.find(
      (so) =>
        so.origin.id === shipOrderShippingPoint.id ||
        so.destination.id === shipOrderShippingPoint.id,
    );

    if (!shipOrder) {
      console.warn(
        "Couldn't find ship order containing ship order shipping point",
        shipOrderShippingPoint,
      );
      return;
    }

    const hasShipOrder = this.stops
      .map((s) => s.shipOrderShippingPoints)
      .flat()
      .map((sosp) => sosp.id)
      .some(
        (id) => id === shipOrder.origin.id || id === shipOrder.destination.id,
      );

    if (!hasShipOrder) {
      const index = this.shipOrders.findIndex((so) => so.id === shipOrder.id);
      this.shipOrders.splice(index, 1);
    }
  }

  updateStopSequence(): void {
    for (let i = 0; i < this.stops.length; i++) {
      this.stops[i].sequenceNumber = i + 1;
    }
  }

  toJSON() {
    // `JSON.stringify` ignores getters so manually add them here if needed
    return {
      identifier: this.identifier,
      shipOrderIds: this.shipOrderIds,
      stops: this.stops,
      lineItems: this.lineItems,
    };
  }
}
